import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentClinicId } from "@/hooks/useAppointments";

export type TemplateCategory = "MARKETING" | "UTILITY" | "AUTHENTICATION";
export type TemplateHeaderType = "TEXT" | "IMAGE" | "VIDEO" | "DOCUMENT";
export type TemplateMetaStatus = "DRAFT" | "PENDING" | "APPROVED" | "REJECTED" | "PAUSED" | "DISABLED";

export interface TemplateButton {
  type: "QUICK_REPLY" | "URL" | "PHONE_NUMBER";
  text: string;
  url?: string;
  phone_number?: string;
}

export interface TemplateVariableMapping {
  index: number;
  label: string;
  source?: string;
}

export interface WhatsAppMessageTemplate {
  id: string;
  clinicId: string;
  name: string;
  language: string;
  category: TemplateCategory;
  headerType: TemplateHeaderType | null;
  headerText: string | null;
  headerMediaUrl: string | null;
  bodyText: string;
  footerText: string | null;
  buttons: TemplateButton[] | null;
  variableMapping: TemplateVariableMapping[];
  metaTemplateId: string | null;
  metaStatus: TemplateMetaStatus;
  metaRejectionReason: string | null;
  metaSubmittedAt: string | null;
  metaSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTemplateInput {
  name: string;
  language: string;
  category: TemplateCategory;
  headerType?: TemplateHeaderType;
  headerText?: string;
  headerMediaUrl?: string;
  bodyText: string;
  footerText?: string;
  buttons?: TemplateButton[];
  variableMapping?: TemplateVariableMapping[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapRow(row: any): WhatsAppMessageTemplate {
  return {
    id: row.id,
    clinicId: row.clinic_id,
    name: row.name,
    language: row.language,
    category: row.category as TemplateCategory,
    headerType: row.header_type ?? null,
    headerText: row.header_text ?? null,
    headerMediaUrl: row.header_media_url ?? null,
    bodyText: row.body_text,
    footerText: row.footer_text ?? null,
    buttons: row.buttons ?? null,
    variableMapping: row.variable_mapping ?? [],
    metaTemplateId: row.meta_template_id ?? null,
    metaStatus: row.meta_status as TemplateMetaStatus,
    metaRejectionReason: row.meta_rejection_reason ?? null,
    metaSubmittedAt: row.meta_submitted_at ?? null,
    metaSyncedAt: row.meta_synced_at ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

const QUERY_KEY = "whatsapp-message-templates";

async function syncPendingTemplatesFromMeta(
  clinicId: string,
  templates: WhatsAppMessageTemplate[]
): Promise<WhatsAppMessageTemplate[]> {
  const pendingTemplates = templates.filter((template) => template.metaStatus === "PENDING");
  if (pendingTemplates.length === 0) return templates;

  const { data, error } = await supabase.functions.invoke("list-whatsapp-templates", {
    body: { clinicId },
  });
  if (error) return templates;

  const metaTemplates = Array.isArray(data?.templates) ? data.templates : [];
  const nowIso = new Date().toISOString();
  const syncedTemplates = await Promise.all(
    templates.map(async (template) => {
      const metaTemplate = metaTemplates.find(
        (row: { name?: string; language?: string }) =>
          row.name === template.name && row.language === template.language
      ) as { id?: string; status?: TemplateMetaStatus; category?: TemplateCategory } | undefined;

      if (!metaTemplate?.status || metaTemplate.status === template.metaStatus) return template;

      const patch = {
        meta_template_id: metaTemplate.id ?? template.metaTemplateId,
        meta_status: metaTemplate.status,
        category: metaTemplate.category ?? template.category,
        meta_synced_at: nowIso,
      };

      const { data: updated, error: updateError } = await supabase
        .from("whatsapp_message_templates" as never)
        .update(patch as never)
        .eq("id", template.id)
        .select()
        .single();

      return updateError ? template : mapRow(updated);
    })
  );

  return syncedTemplates;
}

export function useWhatsAppMessageTemplates() {
  const { data: clinicId } = useCurrentClinicId();

  const hasPending = (templates: WhatsAppMessageTemplate[]) =>
    templates.some((t) => t.metaStatus === "PENDING");

  return useQuery({
    queryKey: [QUERY_KEY, clinicId],
    queryFn: async (): Promise<WhatsAppMessageTemplate[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from("whatsapp_message_templates" as never)
        .select("*")
        .eq("clinic_id", clinicId)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const templates = (data as any[]).map(mapRow);
      return syncPendingTemplatesFromMeta(clinicId, templates);
    },
    enabled: !!clinicId,
    refetchInterval: (query) => {
      const templates = query.state.data;
      if (Array.isArray(templates) && hasPending(templates)) return 15_000;
      return false;
    },
    staleTime: 60_000,
  });
}

export function useCreateWhatsAppTemplate() {
  const { data: clinicId } = useCurrentClinicId();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateTemplateInput) => {
      if (!clinicId) throw new Error("No clinic selected");
      const { data, error } = await supabase
        .from("whatsapp_message_templates" as never)
        .insert({
          clinic_id: clinicId,
          name: input.name,
          language: input.language,
          category: input.category,
          header_type: input.headerType ?? null,
          header_text: input.headerText ?? null,
          header_media_url: input.headerMediaUrl ?? null,
          body_text: input.bodyText,
          footer_text: input.footerText ?? null,
          buttons: input.buttons ?? null,
          variable_mapping: input.variableMapping ?? [],
        } as never)
        .select()
        .single();
      if (error) throw error;
      return mapRow(data);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY, clinicId] }),
  });
}

export function useUpdateWhatsAppTemplate() {
  const { data: clinicId } = useCurrentClinicId();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: Partial<CreateTemplateInput> }) => {
      const patch: Record<string, unknown> = {};
      if (input.name !== undefined) patch.name = input.name;
      if (input.language !== undefined) patch.language = input.language;
      if (input.category !== undefined) patch.category = input.category;
      if (input.headerType !== undefined) patch.header_type = input.headerType;
      if (input.headerText !== undefined) patch.header_text = input.headerText;
      if (input.headerMediaUrl !== undefined) patch.header_media_url = input.headerMediaUrl;
      if (input.bodyText !== undefined) patch.body_text = input.bodyText;
      if (input.footerText !== undefined) patch.footer_text = input.footerText;
      if (input.buttons !== undefined) patch.buttons = input.buttons;
      if (input.variableMapping !== undefined) patch.variable_mapping = input.variableMapping;

      const { data, error } = await supabase
        .from("whatsapp_message_templates" as never)
        .update(patch as never)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return mapRow(data);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY, clinicId] }),
  });
}

export function useDeleteWhatsAppTemplate() {
  const { data: clinicId } = useCurrentClinicId();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("whatsapp_message_templates" as never)
        .update({ deleted_at: new Date().toISOString() } as never)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY, clinicId] }),
  });
}
